import type {
  ContentFeedReactionCreateRequest,
  ContentFeedReactionCreateResponse,
} from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import { contentFeedReactionCreateRequestSchema } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import type { ErrorResponse } from '@sola/shared-utils/httpClient.ts';
import {
  httpDeleteWithStatus,
  httpPostWithStatus,
  isErrorResponse,
  isSuccessStatus,
} from '@sola/shared-utils/httpClient.ts';

import { MY_API_BASE } from '../constants.ts';

// Trailing slash: same CloudFront OAC / trailingSlash reason as entries.
export const routeContentFeedReactions = () =>
  `${MY_API_BASE}/content-feed/reactions/`;

export const routeContentFeedReaction = (reactionId: string) =>
  `${MY_API_BASE}/content-feed/reactions/${reactionId}/`;

/**
 * Creates a reaction on an entry.
 * POST /api/content-feed/reactions/
 *
 * The request body is validated against the shared schema before sending.
 * Uses signRequest for CloudFront OAC compatibility.
 */
export const createReaction = async (
  entryId: string,
  emoji: string,
): Promise<ContentFeedReactionCreateResponse> => {
  const parsed = contentFeedReactionCreateRequestSchema.safeParse({
    entryId,
    emoji,
  });

  if (!parsed.success) {
    throw new Error(`Invalid reaction request: ${emoji}`);
  }

  const { response, statusCode } = await httpPostWithStatus<
    ContentFeedReactionCreateResponse | ErrorResponse
  >(
    routeContentFeedReactions(),
    parsed.data as ContentFeedReactionCreateRequest,
    {
      signRequest: true,
    },
  );

  if (!isSuccessStatus(statusCode) || !response || isErrorResponse(response)) {
    throw new Error(`Failed to create reaction: ${statusCode}`);
  }

  return response;
};

/**
 * Deletes a reaction by ID.
 * DELETE /api/content-feed/reactions/:reactionId/
 *
 * Uses path-based routing (no body on DELETE) and signRequest.
 */
export const deleteReaction = async (reactionId: string): Promise<void> => {
  const { statusCode } = await httpDeleteWithStatus<void>(
    routeContentFeedReaction(reactionId),
    { signRequest: true },
  );

  if (!isSuccessStatus(statusCode)) {
    throw new Error(`Failed to delete reaction: ${statusCode}`);
  }
};

/**
 * Creates a reaction on a comment.
 * POST /api/content-feed/reactions/
 *
 * Same endpoint as entry reactions, but passes commentId instead of entryId.
 */
export const createCommentReaction = async (
  commentId: string,
  emoji: string,
): Promise<ContentFeedReactionCreateResponse> => {
  const parsed = contentFeedReactionCreateRequestSchema.safeParse({
    commentId,
    emoji,
  });

  if (!parsed.success) {
    throw new Error(`Invalid comment reaction request: ${emoji}`);
  }

  const { response, statusCode } = await httpPostWithStatus<
    ContentFeedReactionCreateResponse | ErrorResponse
  >(
    routeContentFeedReactions(),
    parsed.data as ContentFeedReactionCreateRequest,
    {
      signRequest: true,
    },
  );

  if (!isSuccessStatus(statusCode) || !response || isErrorResponse(response)) {
    throw new Error(`Failed to create comment reaction: ${statusCode}`);
  }

  return response;
};
